import { useEffect, useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';

import {
    type CancellationFeedback,
    OTHER_CANCELLATION_FEEDBACK,
    subscriptionMeOptions,
    useCancelPlanOptions,
} from '@/entities/subscription';
import { useSheetStore } from '@/shared/stores/sheet-store';

import { useIsCancellationProcessing } from './use-is-cancellation-processing';

const MANAGE_PLAN_SHEET_TYPE = 'subscription/manage-plan';

export function useCancelPlan() {
    const [isOpen, setIsOpen] = useState(false);
    const [feedback, setFeedback] = useState<CancellationFeedback | null>(null);
    const [comment, setComment] = useState('');

    const closeAllSheets = useSheetStore((state) => state.closeAllSheets);
    const isPanelOpen = useSheetStore(
        (state) => state.currentSheet?.type === MANAGE_PLAN_SHEET_TYPE,
    );
    const { data: subscription } = useQuery(subscriptionMeOptions());
    const cancelPlan = useMutation(useCancelPlanOptions());
    const isProcessing = useIsCancellationProcessing();

    // The sheet host stays mounted across open/close, so reset the flow whenever
    // the panel is closed to avoid a stale view reappearing on the next open.
    useEffect(() => {
        if (!isPanelOpen) {
            setIsOpen(false);
            setFeedback(null);
            setComment('');
        }
    }, [isPanelOpen]);

    // Slide the panel out only once the cancellation has synced back via webhook,
    // not on the bare mutation response.
    useEffect(() => {
        if (cancelPlan.isSuccess && subscription?.cancelAtPeriodEnd) {
            closeAllSheets();
        }
    }, [cancelPlan.isSuccess, subscription?.cancelAtPeriodEnd, closeAllSheets]);

    const open = () => {
        cancelPlan.reset();
        setFeedback(null);
        setComment('');
        setIsOpen(true);
    };

    const close = () => {
        setIsOpen(false);
        setFeedback(null);
        setComment('');
    };

    const isOtherReason = feedback === OTHER_CANCELLATION_FEEDBACK;
    const canConfirm =
        feedback != null && (!isOtherReason || comment.trim().length > 0) && !isProcessing;

    const confirm = () => {
        if (feedback == null || !canConfirm) return;
        cancelPlan.mutate({
            feedback,
            comment: isOtherReason ? comment.trim() : undefined,
        });
    };

    return {
        isOpen,
        open,
        close,
        feedback,
        setFeedback,
        comment,
        setComment,
        isOtherReason,
        confirm,
        canConfirm,
        isProcessing,
        error: cancelPlan.error,
    };
}
